// Completa el negocio.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Reservation from '../interfaces/Reservation'
import { Salenote } from '../interfaces/Salenote'
import { Estimate } from '../interfaces/Estimate'
import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import useSalenotesDealFetcher from '../hooks/useSalenotesDealFetcher'
import useEstimatesDealFetcher from '../hooks/useEstimatesDealFetcher'
import useReservationsDealFetcher from '../hooks/useReservationsDealFetcher'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import { formatCurrency, formatIsoToDDMMYYYY } from '../utils/utils'
import Deal from '../interfaces/Deal'
import { AppPath } from '../enums/AppPath'

import { ButtonAction } from './Buttons'
import EstimateEdit from './EstimateEdit'
import { ReservationNvvEdit } from './ReservationNvvEdit'
import ReservationNvvDetailPage from './ReservationNvvDetailPage'
import SalenoteEdit from './SalenoteEdit'
import SalenoteDetailPage from './SalenoteDetailPage'

// Muestra los documentos que se han generado.
const DealComplete: React.FC<{ deal: Deal; onRefresh: () => void }> = ({ deal, onRefresh }) => {
  const navigate = useNavigate()
  const [salenotes, setSalenotes] = useState<Salenote[]>([])
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [vehicle, setVehicle] = useState<Vehicle>()
  const [client, setClient] = useState<Client>()
  const [salenoteCreaShow, setSalenoteCreaShow] = useState<boolean>(false)
  const [estimateCreaShow, setEstimateCreaShow] = useState<boolean>(false)
  const [reservationCreaShow, setReservationCreaShow] = useState<boolean>(false)

  const { fetchSalenotesDeal, errorSalenotesDeal } = useSalenotesDealFetcher()
  const { fetchEstimatesDeal, errorEstimatesDeal } = useEstimatesDealFetcher()
  const { fetchReservationsDeal, errorReservationsDeal } = useReservationsDealFetcher()

  const { fetchVehicle, errorVehicle } = useVehicleFetcher()
  const { fetchClient, errorClient } = useClientFetcher()
  const [reservationDetailShow, setReservationDetailShow] = useState<boolean>(false)
  const [salenoteDetailShow, setSalenoteDetailShow] = useState<boolean>(false)

  // Contienen el "id" obtenido de la grilla y que se recupera con el "click" de la fila
  const [reservationId, setReservationId] = useState<string>('')
  const [salenoteId, setSalenoteId] = useState<string>('')

  useEffect(() => {
    // Directly call the hook when buttons are not shown
    fetchAsync()
  }, [])

  const fetchAsync = async () => {
    if (!deal) {
      return // Exit early if `id` is undefined
    }
    hideComponents()

    const salenotes = await fetchSalenotesDeal(deal.vehicle, deal.client)
    setSalenotes(salenotes)
    const estimates = await fetchEstimatesDeal(deal.vehicle, deal.client)
    setEstimates(estimates)
    const reservations = await fetchReservationsDeal(deal.vehicle, deal.client)
    setReservations(reservations)

    if (!vehicle) {
      const veh = await fetchVehicle(deal.vehicle)

      if (veh) setVehicle(veh)
    }

    if (!client) {
      const cli = await fetchClient(deal.client)

      if (cli) setClient(cli)
    }
  }

  // Escondemos los componentes adicionales
  const hideComponents = () => {
    setEstimateCreaShow(false)
    setSalenoteCreaShow(false)
    setReservationCreaShow(false)
  }
  // A crear la nota de venta
  const handleNvvCreaClick = async () => {
    setEstimateCreaShow(false)
    setSalenoteCreaShow(true)
    setReservationCreaShow(false)
  }

  // A crear la cotizacion
  const handleCotizaCreaClick = async () => {
    setEstimateCreaShow(true)
    setSalenoteCreaShow(false)
    setReservationCreaShow(false)
  }

  // A crear la reserva
  const handleReservaCreaClick = async () => {
    setEstimateCreaShow(false)
    setSalenoteCreaShow(false)
    setReservationCreaShow(true)
  }
  const handleRowEstimateClick = (id: string) => {
    // Navigate to standalone estimate page with deal context
    navigate(`/${AppPath.Estimates}/view/${id}?fromDeal=${deal._id}`)
  }
  const handleRowReservationClick = (id: string) => {
    setReservationDetailShow(true)
    setReservationId(id)
  }
  const handleRowSalenoteClick = (id: string) => {
    setSalenoteDetailShow(true)
    setSalenoteId(id)
  }
  const onCloseSalenoteDetail = (approve: boolean) => {
    setSalenoteDetailShow(false)

    if (approve) {
      // el usuario presiono aprobar. Tenemos que hacer otra cosa
      // porque aprobar una nota de venta hace que se complete
      // el negocio
      // COn lo siguiente forzamos a que el sistema lea de nuevo el negocio

      onRefresh()
    }
  }
  const onCreateEstimate = async () => {
    fetchAsync()
  }
  const onCreateModifyReservation = () => {
    fetchAsync()
  }
  const onCreateSalenote = () => {
    fetchAsync()
  }

  return (
    <>
      {!estimateCreaShow &&
        !reservationCreaShow &&
        !reservationDetailShow &&
        !salenoteCreaShow &&
        !salenoteDetailShow && (
          <div className="print:hidden">
            <div className="border mt-2">
              {estimates.length === 0 && (
                <div className="flex gap-2">
                  <div className="text-2xl">No hay cotizaciones vigentes para este vehículo y cliente. </div>
                  <ButtonAction label="Crear cotización..." handleButtonClick={handleCotizaCreaClick} />
                </div>
              )}
              {estimates.length > 0 && (
                <div>
                  <div className="text-2xl">Cotizaciones vigentes o expiradas para este vehículo y cliente. </div>
                  <div className="grid grid-cols-4 font-bold mt-2">
                    <div>Número</div>
                    <div>Fecha</div>
                    <div>Fecha de expiración</div>
                    <div>Precio</div>
                  </div>
                  {estimates.map((field) => (
                    <div
                      key={field._id}
                      className="grid grid-cols-4 cursor-pointer"
                      onClick={() => handleRowEstimateClick(field._id)}
                    >
                      <div>{field.number}</div>
                      <div>{formatIsoToDDMMYYYY(field.date)}</div>
                      <div>{formatIsoToDDMMYYYY(field.expiresAt)}</div>
                      <div>{formatCurrency(field.price)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border mt-2">
              {reservations.length === 0 && (
                <div className="flex gap-2">
                  <div className="text-2xl">No hay reservas activas para este vehículo y cliente. </div>
                  <ButtonAction label="Crear reserva..." handleButtonClick={handleReservaCreaClick} />
                </div>
              )}
              {reservations.length > 0 && (
                <div>
                  <div className="text-2xl">Reservas activas para este vehículo y cliente. </div>
                  <div className="grid grid-cols-3 font-bold mt-2">
                    <div>Número</div>
                    <div>Fecha</div>
                    <div>Precio</div>
                  </div>
                  {reservations.map((field) => (
                    <div
                      key={field._id}
                      className="grid grid-cols-3 cursor-pointer"
                      onClick={() => handleRowReservationClick(field._id)}
                    >
                      <div>{field.number}</div>
                      <div>{formatIsoToDDMMYYYY(field.date)}</div>
                      <div>{formatCurrency(field.price)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border mt-2">
              {salenotes.length === 0 && (
                <div className="flex gap-2">
                  <div className="text-2xl">
                    No hay notas de venta pendientes o aprobadas para este vehículo y cliente.{' '}
                  </div>
                  <ButtonAction label="Crear nota de venta..." handleButtonClick={handleNvvCreaClick} />
                </div>
              )}
              {salenotes.length > 0 && (
                <div>
                  <div className="text-2xl">Notas de venta pendientes o aprobadas para este vehículo y cliente. </div>
                  <div className="grid grid-cols-4 font-bold mt-2">
                    <div>Número</div>
                    <div>Fecha nota de venta</div>
                    <div>Fecha de aprobación</div>
                    <div>Precio</div>
                  </div>
                  {salenotes.map((field) => (
                    <div
                      key={field._id}
                      className="grid grid-cols-4 cursor-pointer"
                      onClick={() => handleRowSalenoteClick(field._id)}
                    >
                      <div>{field.number}</div>
                      <div>{formatIsoToDDMMYYYY(field.date)}</div>
                      <div>{formatIsoToDDMMYYYY(field.approvedAt)}</div>
                      <div>{formatCurrency(field.price)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      {estimateCreaShow && !!vehicle && !!client && (
        <div>
          <div className="text-2xl font-bold flex justify-center">Creando cotización.</div>
          <EstimateEdit vehicle={vehicle} client={client} onCreate={onCreateEstimate} deal={deal} />
        </div>
      )}
      {reservationCreaShow && !!vehicle && !!client && (
        <div>
          <div className="text-2xl font-bold flex justify-center">Creando reserva.</div>
          <ReservationNvvEdit
            vehicle={vehicle}
            client={client}
            nvvInd={0}
            onCreateModify={onCreateModifyReservation}
            deal={deal}
          />
        </div>
      )}
      {salenoteCreaShow && !!vehicle && !!client && (
        <SalenoteEdit vehicle={vehicle} client={client} onCreate={onCreateSalenote} deal={deal} />
      )}
      {reservationDetailShow && <ReservationNvvDetailPage id={reservationId} />}
      {salenoteDetailShow && <SalenoteDetailPage salenoteId={salenoteId} onClose={onCloseSalenoteDetail} />}
      <div>{errorSalenotesDeal?.message}</div>
      <div>{errorEstimatesDeal?.message}</div>
      <div>{errorReservationsDeal?.message}</div>
      <div>{errorVehicle?.message}</div>
      <div>{errorClient?.message}</div>
    </>
  )
}
export default DealComplete
